import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Map,
  MapPin,
  Zap,
  Code,
  Truck,
  ChevronRight,
  FileText,
  Video,
  Briefcase,
  Bell,
  Users,
  Mail,
  Lock,
  Globe,
  Shield,
  ShoppingBag,
} from "lucide-react";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 992 && menuOpen) {
        setMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [menuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuOpen &&
        !event.target.closest("nav") &&
        !event.target.closest(".menu-toggle")
      ) {
        setMenuOpen(false);
        setActiveDropdown(null);
        document.body.style.overflow = "auto";
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
    document.body.style.overflow = "auto";
  }, [location]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    if (!menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  };

  const toggleDropdown = (name, e) => {
    if (windowWidth < 992) {
      e.preventDefault();
      setActiveDropdown(activeDropdown === name ? null : name);
    }
  };

  const getLogoSize = () => {
    if (windowWidth <= 576) {
      return { maxHeight: "80px", width: "160px" };
    } else {
      return { maxHeight: "120px", width: "220px" };
    }
  };

  const isParentActive = (paths) => {
    return paths.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(path + "/"),
    );
  };

  return (
    <header className="modern-header">
      <div className={`navbar-modern ${scrolled ? "header-scrolled" : ""}`}>
        <div className="container-fluid px-3 px-lg-5">
          <div className="header-wrapper">
            <div className="logo-container">
              <Link to="/" className="logo">
                <img
                  src="/img/logos/geosentry-logo-color-390.svg"
                  alt="Geosentry Logo - GPS Tracking and Geofencing Platform"
                  className="main-logo"
                  style={{ ...getLogoSize(), filter: "none" }}
                />
              </Link>
            </div>

            <button
              className={`menu-toggle ${menuOpen ? "active" : ""}`}
              onClick={toggleMenu}
              aria-label="Toggle navigation"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            <nav className={`main-nav ${menuOpen ? "open" : ""}`}>
              <ul className="nav-links">
                {/* Product */}
                <li
                  className={`has-dropdown ${activeDropdown === "product" ? "dropdown-open" : ""}`}
                >
                  <span
                    className={`dropdown-toggle nav-link-style ${isParentActive([
                      "/service/fleet-management",
                      "/service/api-sdk",
                      "/logistics",
                      "/service/gps-tracking",
                      "/service/geofencing",
                      "/service/location-intelligence",
                      "/service/geotag",
                      "/product/justaround",
                    ])
                      ? "active"
                      : ""
                      }`}
                    onClick={(e) => toggleDropdown("product", e)}
                  >
                    Product
                  </span>
                  <ul
                    className={`dropdown-menu rich-dropdown ${activeDropdown === "product" ? "show" : ""
                      }`}
                  >
                    {[

                      {
                        title: "API & SDK",
                        desc: "Developer friendly tools and documentation",
                        icon: <Code size={20} className="text-primary" />,
                        path: "/service/api-sdk",
                      },
                      {
                        title: "Location Intelligence",
                        desc: "Turn location data into actionable insights",
                        icon: <Globe size={20} className="text-secondary" />,
                        path: "/service/location-intelligence",
                      },
                      {
                        title: "Fleet Management",
                        desc: "Optimize pickup, delivery, and workforce",
                        icon: <Zap size={20} className="text-warning" />,
                        path: "/service/fleet-management",
                      },
                      {
                        title: "Geofencing",
                        desc: "Hyper-accurate user positioning and alerts",
                        icon: <Map size={20} className="text-success" />,
                        path: "/service/geofencing",
                      },
                      {
                        title: "GPS Tracking",
                        desc: "Real-time location, routing, and maps",
                        icon: <MapPin size={20} className="text-info" />,
                        path: "/service/gps-tracking",
                      },
                      {
                        title: "Geo Smart Tag",
                        desc: "Smart vehicle identity and instant contact",
                        icon: <Shield size={20} className="text-danger" />,
                        path: "/service/geotag",
                      },
                      {
                        title: "JustAround",
                        desc: "Neighborhood marketplace for sharing items and services",
                        icon: <ShoppingBag size={20} className="text-warning" />,
                        path: "/product/justaround",
                      },
                      {
                        title: "Logistics",
                        desc: "Streamline supply chain operations",
                        icon: <Truck size={20} className="text-success" />,
                        path: "/logistics",
                      },
                    ].map((item, index) => (
                      <li key={index}>
                        <NavLink
                          to={item.path}
                          className="rich-nav-link"
                          onClick={() => windowWidth < 992 && toggleMenu()}
                        >
                          <div className="nav-icon-wrapper">{item.icon}</div>
                          <div className="nav-text-content">
                            <span className="nav-item-title">
                              {item.title}
                              <ChevronRight size={14} className="nav-arrow" />
                            </span>
                            <span className="nav-item-desc">{item.desc}</span>
                          </div>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>

                <li
                  className={
                    location.pathname === "/developers" ? "active" : ""
                  }
                >
                  <NavLink
                    to="/developers"
                    onClick={() => windowWidth < 992 && toggleMenu()}
                  >
                    Developers
                  </NavLink>
                </li>

                {/* Pricing */}
                <li
                  className={location.pathname === "/pricing" ? "active" : ""}
                >
                  <NavLink
                    to="/pricing"
                    onClick={() => windowWidth < 992 && toggleMenu()}
                  >
                    Pricing
                  </NavLink>
                </li>

                {/* Resources */}
                <li
                  className={`has-dropdown ${activeDropdown === "resources" ? "dropdown-open" : ""
                    }`}
                >
                  <span
                    className={`dropdown-toggle nav-link-style ${isParentActive(["/blogs", "/video", "/stories", "/news"])
                      ? "active"
                      : ""
                      }`}
                    onClick={(e) => toggleDropdown("resources", e)}
                  >
                    Resources
                  </span>
                  <ul
                    className={`dropdown-menu rich-dropdown ${activeDropdown === "resources" ? "show" : ""
                      }`}
                  >
                    {[
                      {
                        title: "Blogs",
                        desc: "Latest insights, guides, and articles",
                        icon: <FileText size={20} className="text-primary" />,
                        path: "/blogs",
                      },
                      {
                        title: "Videos & Demos",
                        desc: "Watch product tours and tutorials",
                        icon: <Video size={20} className="text-danger" />,
                        path: "/video",
                      },
                      {
                        title: "Case Studies",
                        desc: "Success stories from our customers",
                        icon: <Briefcase size={20} className="text-info" />,
                        path: "/stories",
                      },
                      {
                        title: "News & Updates",
                        desc: "Latest announcements and features",
                        icon: <Bell size={20} className="text-warning" />,
                        path: "/news",
                      },
                    ].map((item, index) => (
                      <li key={index}>
                        <NavLink
                          to={item.path}
                          className="rich-nav-link"
                          onClick={() => windowWidth < 992 && toggleMenu()}
                        >
                          <div className="nav-icon-wrapper">{item.icon}</div>
                          <div className="nav-text-content">
                            <span className="nav-item-title">
                              {item.title}
                              <ChevronRight size={14} className="nav-arrow" />
                            </span>
                            <span className="nav-item-desc">{item.desc}</span>
                          </div>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>

                {/* Company */}
                <li
                  className={`has-dropdown dropdown-align-right ${activeDropdown === "company" ? "dropdown-open" : ""
                    }`}
                >
                  <span
                    className={`dropdown-toggle nav-link-style ${isParentActive(["/about", "/contact", "/privacy-policy"])
                      ? "active"
                      : ""
                      }`}
                    onClick={(e) => toggleDropdown("company", e)}
                  >
                    Company
                  </span>
                  <ul
                    className={`dropdown-menu rich-dropdown ${activeDropdown === "company" ? "show" : ""
                      }`}
                  >
                    {[
                      {
                        title: "About Us",
                        desc: "Our mission, vision, and team",
                        icon: <Users size={20} className="text-primary" />,
                        path: "/about",
                      },
                      {
                        title: "Contact Us",
                        desc: "Get in touch with our support team",
                        icon: <Mail size={20} className="text-success" />,
                        path: "/contact",
                      },
                      {
                        title: "Privacy Policy",
                        desc: "How we protect your data and privacy",
                        icon: <Lock size={20} className="text-info" />,
                        path: "/privacy-policy",
                      },
                    ].map((item, index) => (
                      <li key={index}>
                        <NavLink
                          to={item.path}
                          className="rich-nav-link"
                          onClick={() => windowWidth < 992 && toggleMenu()}
                        >
                          <div className="nav-icon-wrapper">{item.icon}</div>
                          <div className="nav-text-content">
                            <span className="nav-item-title">
                              {item.title}
                              <ChevronRight size={14} className="nav-arrow" />
                            </span>
                            <span className="nav-item-desc">{item.desc}</span>
                          </div>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>

                {/* CTA Buttons */}
                <li className="header-cta-item">
                  <a
                    href="https://app.geosentry.ai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => windowWidth < 992 && toggleMenu()}
                    className="login-btn"
                  >
                    Login
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
